import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { User, Wrench, RefreshCw, FileText, Fuel, Image as ImageIcon, Trash2 } from 'lucide-react';
import { getApiUrl } from '@/config';

interface VehicleHistoryEntry {
    _id: string;
    type: 'assignment' | 'status_change' | 'maintenance' | 'note' | 'fuel';
    technicianName?: string;
    previousStatus?: string;
    newStatus?: string;
    description?: string;
    km?: number;
    date: string;
    // Fuel specific
    cost?: number;
    litres?: number;
    proofImage?: string;
}

interface VehicleHistoryListProps {
    vehicleId: string;
}

export const VehicleHistoryList: React.FC<VehicleHistoryListProps> = ({ vehicleId }) => {
    const [history, setHistory] = useState<VehicleHistoryEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    // Initial check for admin role
    const [isAdmin, setIsAdmin] = useState(false);
    useEffect(() => {
        try {
            const userStr = localStorage.getItem('currentUser');
            if (userStr) {
                const user = JSON.parse(userStr);
                if (user.role === 'admin') setIsAdmin(true);
            }
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const token = localStorage.getItem('token');
                const headers: Record<string, string> = {};
                if (token) headers['Authorization'] = `Bearer ${token}`;

                const res = await fetch(getApiUrl(`/api/vehicles/${vehicleId}/history`), { headers });
                if (res.ok) {
                    const data = await res.json();
                    setHistory(data);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (vehicleId) fetchHistory();
    }, [vehicleId]);

    const handleDelete = async (entryId: string) => {
        if (!confirm('Êtes-vous sûr de vouloir supprimer cette entrée d\'historique ?')) return;

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl(`/api/vehicles/${vehicleId}/history/${entryId}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                setHistory(prev => prev.filter(h => h._id !== entryId));
            } else {
                alert('Erreur lors de la suppression');
            }
        } catch (e) {
            console.error(e);
            alert('Erreur réseau');
        }
    };

    if (loading) return <div className="text-center py-4 text-gray-500">Chargement de l'historique...</div>;
    if (history.length === 0) return <div className="text-center py-4 text-gray-500">Aucun historique disponible.</div>;

    return (
        <div className="space-y-4 relative">
            {/* Image Preview Modal */}
            {previewImage && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-pointer"
                    onClick={() => setPreviewImage(null)}
                >
                    <img src={previewImage} alt="Ticket" className="max-w-full max-h-[90vh] rounded-lg" />
                </div>
            )}

            {history.map((entry) => (
                <div key={entry._id} className="relative pl-6 pb-4 border-l border-white/10 last:pb-0 group">
                    <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 
                        ${entry.type === 'assignment' ? 'bg-blue-500/20 border-blue-500' :
                            entry.type === 'maintenance' ? 'bg-orange-500/20 border-orange-500' :
                                entry.type === 'status_change' ? 'bg-purple-500/20 border-purple-500' :
                                    entry.type === 'fuel' ? 'bg-amber-500/20 border-amber-500' :
                                        'bg-gray-500/20 border-gray-500'}`}
                    ></div>

                    {/* Delete Button (Admin Only) */}
                    {isAdmin && (
                        <button
                            onClick={() => handleDelete(entry._id)}
                            className="absolute right-0 top-0 opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-red-400 transition-all hover:bg-white/5 rounded"
                            title="Supprimer cette entrée"
                        >
                            <Trash2 size={14} />
                        </button>
                    )}

                    <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between text-xs text-gray-400 pr-6">
                            <span>{format(new Date(entry.date), 'dd/MM/yyyy HH:mm', { locale: fr })}</span>
                            {entry.km && <span>{entry.km.toLocaleString()} km</span>}
                        </div>

                        <div className="text-sm font-medium text-white">
                            {entry.type === 'assignment' && (
                                <div className="flex items-center gap-2">
                                    <User size={14} className="text-blue-400" />
                                    <span>Assigné à <span className="text-blue-300">{entry.technicianName || 'Inconnu'}</span></span>
                                </div>
                            )}
                            {entry.type === 'maintenance' && (
                                <div className="flex items-center gap-2">
                                    <Wrench size={14} className="text-orange-400" />
                                    <span>Maintenance / Réparation</span>
                                </div>
                            )}
                            {entry.type === 'status_change' && (
                                <div className="flex items-center gap-2">
                                    <RefreshCw size={14} className="text-purple-400" />
                                    <span>Changement de statut</span>
                                </div>
                            )}
                            {entry.type === 'note' && (
                                <div className="flex items-center gap-2">
                                    <FileText size={14} className="text-gray-400" />
                                    <span>Note</span>
                                </div>
                            )}
                            {entry.type === 'fuel' && (
                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center gap-2">
                                        <Fuel size={14} className="text-amber-400" />
                                        <span>Plein Carburant</span>
                                    </div>
                                    <div className="text-xs text-gray-300 ml-6 flex gap-3">
                                        {entry.litres && <span>{entry.litres} L</span>}
                                        {entry.cost && <span>{entry.cost} €</span>}
                                        {entry.proofImage && (
                                            <button
                                                onClick={() => setPreviewImage(entry.proofImage || null)}
                                                className="flex items-center gap-1 text-blue-400 hover:text-blue-300 hover:underline"
                                            >
                                                <ImageIcon size={10} />
                                                Voir Ticket
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {entry.previousStatus && entry.newStatus && entry.previousStatus !== entry.newStatus && (
                            <div className="text-xs text-gray-500 flex items-center gap-2">
                                <span className="line-through opacity-70">{entry.previousStatus}</span>
                                <span>→</span>
                                <span className="text-gray-300">{entry.newStatus}</span>
                            </div>
                        )}

                        {entry.description && entry.type !== 'fuel' && (
                            <div className="text-sm text-gray-400 bg-white/5 p-2 rounded mt-1">
                                {entry.description}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};
